import {useId, useState} from 'react';
import type {AccountCreditBalance} from '../src/account-credit-types';
import {ErrorNotice, Loading} from './components';
import type {HermesState, NativeTokenSnapshot} from './lib/types';
import type {TeamMemberView} from './team-workspace';
import {StorageUsage} from './storage-usage';
import './workspace-usage.css';
import {APP_BASE,Link} from './router';
import {errorPresentation} from './lib/error-presentation';

const tokenNumber = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});
const dollars = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2});

function currentTokens(state: HermesState): NativeTokenSnapshot | null {
  const snapshot = state.nativeTokens.data;
  return state.nativeTokens.status === 'ready' && state.nativeConnection.status === 'open'
    && snapshot?.sessionId === state.nativeTarget?.sessionId ? snapshot : null;
}

type ReadyBalance=Extract<AccountCreditBalance,{status:'ready'}>;
function CreditMeter({balance}:{balance:ReadyBalance}) {
  const meter=balance.creditBalance.meter;
  const percent=new Intl.NumberFormat(undefined,{maximumFractionDigits:1}).format(meter.remainingPercent);
  return <div className="credit-meter-summary">
    <div className="credit-meter-heading"><span>{meter.resetsAt?'This week':'Available balance'}</span><strong>{percent}% <span>left</span></strong></div>
    <div className="credit-meter" role="progressbar" aria-label={meter.resetsAt?'Balance available this week':'Available balance'}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={meter.remainingPercent}
      aria-valuetext={`${percent}% remaining${meter.hasExtra?', including extra balance':''}`}>
      <span className="credit-meter-weekly" style={{width:`${meter.weeklyPercent}%`}}/>
      <span className="credit-meter-extra" style={{width:`${meter.extraPercent}%`}}/>
    </div>
    <div className="credit-meter-caption">{meter.resetsAt&&<span>Resets {new Date(meter.resetsAt).toLocaleDateString(undefined,{month:'short',day:'numeric'})}</span>}
      {meter.hasExtra&&<span className="credit-meter-extra-label">Extra balance</span>}</div>
  </div>;
}

export function WorkspaceBalance({state}: {state: HermesState}) {
  return <section className="workspace-balance" aria-label="Available balance">
    <ErrorNotice error={state.accountBalance.error}/>
    {state.accountBalance.data?.status==='ready'&&<Link className="workspace-balance-link" href={`${APP_BASE}/billing`} aria-label="View credit totals and billing"><CreditMeter balance={state.accountBalance.data}/></Link>}
  </section>;
}

export function BotSessionTokens({state, member}: {state: HermesState; member: TeamMemberView}) {
  const snapshot = currentTokens(state);
  const usage = snapshot?.agents.find(agent => agent.sessionId === member.sessionId);
  if (!usage) return null;
  const description = `${tokenNumber.format(usage.inputTokens)} input tokens and ${tokenNumber.format(usage.outputTokens)} output tokens reported in this chat. The current model response is not counted until the model provider reports its usage.`;
  return <span className="bot-session-tokens" title={usage.costEstimate.status === 'error'
    ? `${description}\n${errorPresentation(usage.costEstimate.error,'The cost estimate is unavailable').title}. Open the chat to view Details.` : description}>
      {tokenNumber.format(usage.totalTokens)} tokens
      {usage.costEstimate.status === 'ready' && <> / {dollars.format(usage.costEstimate.costUsd)}</>}
  </span>;
}

export function BotSessionCostError({state,sessionId}:{state:HermesState;sessionId:string|null}){
  const snapshot=currentTokens(state);
  const estimate=sessionId===null?snapshot?.costEstimate:snapshot?.agents.find(agent=>agent.sessionId===sessionId)?.costEstimate;
  return <ErrorNotice title="The cost estimate is unavailable" error={estimate?.status==='error'?estimate.error:null}/>;
}

export function CreditAllowance({state, showError = true}:{state:HermesState; showError?: boolean}) {
  const resource=state.accountBalance,credit=resource.data;
  return <section className="credit-allowance" aria-label="Available balance">
    {showError && <ErrorNotice error={resource.error}/>}<Loading active={resource.status==='loading'&&!credit}/>
    {credit?.status==='ready'&&<><CreditMeter balance={credit}/><Link className="credit-allowance-note" href={`${APP_BASE}/billing`}>View balance</Link></>}
    {credit?.status==='not-funded'&&<Link className="credit-allowance-note" href={`${APP_BASE}/subscribe?new_signup=1`}>Choose a plan</Link>}
  </section>;
}

export function WorkspaceUsage({state}: {state: HermesState}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const credit = state.accountBalance.data;
  const storage = state.nativeRuntime.data?.storage;
  const usedMegabytes = storage?.allocated && state.nativeRuntime.status !== 'error'
    ? new Intl.NumberFormat(undefined, {maximumFractionDigits: 2}).format((storage.personal?.usedBytes ?? storage.usedBytes) / 1_000_000) : null;
  return <div className="workspace-usage" aria-label="Credits and storage">
    <button className="workspace-usage-toggle" type="button" aria-expanded={expanded} aria-controls={detailsId}
      onClick={() => setExpanded(value => !value)}>
      <span className="workspace-usage-summary">
        {!expanded && credit?.status === 'ready' ? `${new Intl.NumberFormat(undefined,{maximumFractionDigits:1}).format(credit.creditBalance.meter.remainingPercent)}% remaining` : 'Balance and storage'}
        {!expanded && usedMegabytes !== null && <span> · {usedMegabytes} MB used</span>}
      </span>
      <span className="workspace-usage-action">Usage<svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg></span>
    </button>
    <div id={detailsId} className={`workspace-usage-details${expanded ? ' is-expanded' : ''}`}>
      <CreditAllowance state={state} showError={false}/><StorageUsage state={state} compact showError={false}/>
    </div>
    <ErrorNotice error={state.accountBalance.error}/>
  </div>;
}
